import { shopifyCdnImageUrl } from './connector-result-card';

/**
 * The store briefing the owner sees after "Show me". The server copies every value from the saved study, so
 * the model never types a number.
 *
 * - No `facts` means a store with no orders: clients show the "no zeros" message, never three zero figures.
 * - `gaps` counts are among the `checked` products, never catalog-wide.
 */

const MAX_FACTS = 3;
const MAX_NEXT_STEPS = 3;
const MAX_GAP_PRODUCTS = 50;
const MAX_CARD_BYTES = 32 * 1024;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;
function object(raw) {
  return raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? raw : undefined;
}
function text(raw, max) {
  return typeof raw === 'string' && raw.trim().length > 0 && raw.length <= max ? raw : undefined;
}
function count(raw) {
  return typeof raw === 'number' && Number.isSafeInteger(raw) && raw >= 0 ? raw : undefined;
}
function readFact(raw) {
  const fact = object(raw);
  const label = text(fact == null ? void 0 : fact.label, 120);
  const value = text(fact == null ? void 0 : fact.value, 40);
  if (!label || !value) {
    return undefined;
  }
  return {
    label,
    value,
    unit: text(fact == null ? void 0 : fact.unit, 16),
    comparison: text(fact == null ? void 0 : fact.comparison, 120)
  };
}
function readNextStep(raw) {
  const step = object(raw);
  const label = text(step == null ? void 0 : step.label, 80);
  const message = text(step == null ? void 0 : step.message, 500);
  return label && message ? {
    label,
    message
  } : undefined;
}
function readLowStock(raw) {
  const stock = object(raw);
  const productId = text(stock == null ? void 0 : stock.productId, 256);
  const title = text(stock == null ? void 0 : stock.title, 512);
  if (!productId || !title || typeof (stock == null ? void 0 : stock.left) !== 'number' || !Number.isSafeInteger(stock.left)) {
    return undefined;
  }
  // An oversold product reports negative stock; the owner has none left to sell.
  return {
    productId,
    title,
    left: Math.max(0, stock.left),
    imageUrl: shopifyCdnImageUrl(stock.imageUrl)
  };
}
function readProductRefs(raw) {
  if (!Array.isArray(raw) || raw.length > MAX_GAP_PRODUCTS) {
    return undefined;
  }
  const refs = [];
  for (const entry of raw) {
    const ref = object(entry);
    const id = text(ref == null ? void 0 : ref.id, 256);
    const title = text(ref == null ? void 0 : ref.title, 512);
    if (!id || !title || refs.some(seen => seen.id === id)) {
      return undefined;
    }
    refs.push({
      id,
      title
    });
  }
  return refs;
}
function readGaps(raw) {
  const gaps = object(raw);
  const checked = count(gaps == null ? void 0 : gaps.checked);
  const missingDescription = readProductRefs(gaps == null ? void 0 : gaps.missingDescription);
  const missingImage = readProductRefs(gaps == null ? void 0 : gaps.missingImage);
  if (checked === undefined || !missingDescription || !missingImage || missingDescription.length > checked || missingImage.length > checked) {
    return undefined;
  }
  return {
    checked,
    missingDescription,
    missingImage
  };
}

/**
 * Validates a `shopifyBriefingCard` read off the wire. A card without a version or a Shopify source is refused
 * whole. Anything else that is malformed is left out on its own: a wrong gap count is worse than no gap row.
 */
export function readShopifyBriefingCard(value) {
  const card = object(value);
  if (!card) {
    return undefined;
  }
  try {
    if (new TextEncoder().encode(JSON.stringify(card)).length > MAX_CARD_BYTES) {
      return undefined;
    }
  } catch {
    return undefined;
  }
  const source = object(card.source);
  if (card.version !== 1 || typeof (source == null ? void 0 : source.storeDomain) !== 'string' || !/^[a-z0-9][a-z0-9-]{0,59}\.myshopify\.com$/.test(source.storeDomain) || typeof source.readAt !== 'string' || !ISO_DATE_TIME.test(source.readAt) || !Number.isFinite(Date.parse(source.readAt))) {
    return undefined;
  }
  const wireFacts = Array.isArray(card.facts) ? card.facts : [];
  const facts = wireFacts.map(readFact).filter(fact => fact !== undefined).slice(0, MAX_FACTS);
  const lowStock = readLowStock(card.lowStock);
  // Facts the server sent but we could not read must not pass for a store with no orders.
  if (wireFacts.length > 0 && facts.length === 0 && !lowStock) {
    return undefined;
  }
  return {
    version: 1,
    facts,
    lowStock,
    gaps: readGaps(card.gaps),
    nextSteps: (Array.isArray(card.nextSteps) ? card.nextSteps : []).map(readNextStep).filter(step => step !== undefined).slice(0, MAX_NEXT_STEPS),
    source: {
      storeDomain: source.storeDomain,
      readAt: source.readAt,
      storeName: text(source.storeName, 512),
      coverage: text(source.coverage, 1024)
    }
  };
}